import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Collapse,
    FormControlLabel,
    IconButton,
    Typography,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import FolderIcon from '@mui/icons-material/Folder';
import GroupIcon from '@mui/icons-material/Group';
import { When } from "react-if";
import { BrowseGroup } from "../../../../services/compose-search.types";
import { GT_COLORS } from "../../../../constants/designTokens.constants";

interface Props {
    browseData: BrowseGroup[];
    expandedGroups: string[];
    onToggleExpand: (groupId: string) => void;
    selectedGroupIds: string[];
    selectedTeamIds: string[];
    onGroupToggle: (groupId: string) => void;
    onTeamToggle: (teamId: string) => void;
    disabled: boolean;
}

export const BrowseHierarchyComponent: React.FC<Props> = ({
    browseData,
    expandedGroups,
    onToggleExpand,
    selectedGroupIds,
    selectedTeamIds,
    onGroupToggle,
    onTeamToggle,
    disabled,
}) => {
    const isGroupSelected = (groupId: string): boolean => {
        return selectedGroupIds.includes(groupId);
    };

    const isGroupIndeterminate = (group: BrowseGroup): boolean => {
        // Only show indeterminate if the group itself is NOT selected,
        // but some of its teams are selected
        if (selectedGroupIds.includes(group.id)) return false;

        const selectedTeamCount = group.teams.filter(t => selectedTeamIds.includes(t.id)).length;
        return selectedTeamCount > 0 && selectedTeamCount < group.teams.length;
    };

    const isTeamSelected = (teamId: string, parentGroupId: string): boolean => {
        // Team is selected if it's directly selected OR its parent group is selected
        return selectedTeamIds.includes(teamId) || selectedGroupIds.includes(parentGroupId);
    };

    const isTeamDisabled = (parentGroupId: string): boolean => {
        // Team checkbox is disabled when parent group is selected (implied by group)
        // or when the whole component is disabled (selectAll mode)
        return disabled || selectedGroupIds.includes(parentGroupId);
    };

    return (
        <Card
            elevation={0}
            sx={{
                height: '400px',
                overflow: 'auto',
                mb: 3,
                border: `1px solid ${GT_COLORS.border}`,
                borderRadius: 2.5,
            }}
        >
            <CardContent>
                <When condition={browseData.length === 0}>
                    <Typography sx={{ fontSize: '0.875rem', color: GT_COLORS.textMuted, fontStyle: 'italic' }}>
                        No groups available
                    </Typography>
                </When>

                {browseData.map((group) => (
                    <Box key={group.id} sx={{ mb: 0.5 }}>
                        {/* Group Row */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                backgroundColor: GT_COLORS.pageBg,
                                borderRadius: 1,
                                p: 0,
                                mb: 0.5,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={() => onToggleExpand(group.id)}
                                sx={{ mr: 1, color: GT_COLORS.textMuted }}
                            >
                                {expandedGroups.includes(group.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            </IconButton>
                            <FolderIcon sx={{ mr: 1, color: GT_COLORS.textMuted, fontSize: '1.2rem' }} />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={disabled || isGroupSelected(group.id)}
                                        indeterminate={!disabled && isGroupIndeterminate(group)}
                                        onChange={() => onGroupToggle(group.id)}
                                        disabled={disabled}
                                        sx={{ '&.Mui-checked': { color: GT_COLORS.primary } }}
                                    />
                                }
                                label={`${group.name} (${group.teams.length} teams)`}
                                sx={{ flexGrow: 1, mr: 0 }}
                            />
                        </Box>

                        {/* Teams */}
                        <Collapse in={expandedGroups.includes(group.id)}>
                            <Box sx={{ ml: 4 }}>
                                {group.teams.map((team) => (
                                    <Box
                                        key={team.id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            mb: 0.5,
                                            backgroundColor: GT_COLORS.headerBg,
                                            borderRadius: 1,
                                            pl: 1,
                                            border: `1px solid ${GT_COLORS.borderLight}`,
                                        }}
                                    >
                                        <GroupIcon sx={{ mr: 1, color: GT_COLORS.textMuted, fontSize: '1rem', ml: 4 }} />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={disabled || isTeamSelected(team.id, group.id)}
                                                    onChange={() => onTeamToggle(team.id)}
                                                    disabled={isTeamDisabled(group.id)}
                                                    sx={{ '&.Mui-checked': { color: GT_COLORS.primary } }}
                                                />
                                            }
                                            label={`${team.name} (${team.userCount} users)`}
                                            sx={{ flexGrow: 1 }}
                                        />
                                    </Box>
                                ))}
                                <When condition={group.teams.length === 0}>
                                    <Typography sx={{ ml: 2, fontStyle: 'italic', fontSize: '0.8rem', color: GT_COLORS.textMuted }}>
                                        No teams in this group
                                    </Typography>
                                </When>
                            </Box>
                        </Collapse>
                    </Box>
                ))}
            </CardContent>
        </Card>
    );
};
